import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";

const ThemeContext = createContext({
  theme: "system", // "light" | "dark" | "system"
  resolvedTheme: "light", // "light" | "dark"
  isDark: false,
  isPublicRoute: false,
  setTheme: () => {},
  toggleTheme: () => {},
});

const THEME_STORAGE_KEY = "app_theme";

// Helper to determine if the route is a public marketing/auth/policy page
export const isPublicRoutePath = (pathname = "") => {
  if (!pathname) return false;
  const p = pathname.toLowerCase();
  return (
    p === "/" ||
    p === "/login" ||
    p.startsWith("/privacy-policy") ||
    p.startsWith("/terms-and-conditions") ||
    p.startsWith("/delete-account") ||
    p.startsWith("/account-deletion")
  );
};

export const ThemeProvider = ({ children }) => {
  let location = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    location = useLocation();
  } catch {
    // Fallback if rendered outside router
    location = typeof window !== "undefined" ? window.location : { pathname: "/" };
  }

  const currentPath = location?.pathname || (typeof window !== "undefined" ? window.location.pathname : "/");
  const isPublic = isPublicRoutePath(currentPath);

  const [theme, setThemeState] = useState(() => {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === "light" || stored === "dark" || stored === "system") {
        return stored;
      }
    } catch {
      // ignore
    }
    return "system";
  });

  const [systemDark, setSystemDark] = useState(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    }
    return false;
  });

  // Listen to system theme changes
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setSystemDark(e.matches);

    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  const rawResolvedTheme = useMemo(() => {
    if (theme === "system") {
      return systemDark ? "dark" : "light";
    }
    return theme;
  }, [theme, systemDark]);

  // If on a public route, disable dark mode functionality and enforce clean light SaaS theme
  const resolvedTheme = isPublic ? "light" : rawResolvedTheme;
  const isDark = !isPublic && rawResolvedTheme === "dark";

  // Apply or remove .dark class on <html> document element
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    if (isDark) {
      root.classList.add("dark");
      root.style.colorScheme = "dark";
    } else {
      root.classList.remove("dark");
      root.style.colorScheme = "light";
    }
  }, [isDark]);

  const setTheme = (newTheme) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    } catch {
      // ignore
    }
    setThemeState(newTheme);
  };

  const toggleTheme = () => {
    // If currently dark, toggle to light; otherwise toggle to dark
    if (isPublic) return; // Disabled on public routes
    setTheme(isDark ? "light" : "dark");
  };

  const value = useMemo(
    () => ({
      theme,
      resolvedTheme,
      isDark,
      isPublicRoute: isPublic,
      setTheme,
      toggleTheme,
    }),
    [theme, resolvedTheme, isDark, isPublic]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export default ThemeContext;
